import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useRetryFetch from "../../hooks/useRetryFetch";
import { Button } from "../ui/button";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { getOnboardingProgram, Program } from "../../api/programs";

const DISMISS_KEY = "onboarding_hero_dismissed";

/**
 * Homepage hero for a user's active onboarding program (e.g. assigned when they joined
 * a mentor's segment via referral link). Renders nothing if there's no active
 * onboarding program or the user dismissed it.
 */
function OnboardingHero() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const [program, setProgram] = useState<Program | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { program } = await getOnboardingProgram(retryFetch);
        if (cancelled || !program) return;
        // Hide once the program is fully done.
        if (program.status === "completed") return;
        // Respect a per-program dismissal.
        const dismissedId = localStorage.getItem(DISMISS_KEY);
        if (dismissedId === program._id) {
          setDismissed(true);
        }
        setProgram(program);
      } catch {
        // Silent — the hero is non-critical.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount. retryFetch is a fresh function each render, so it must not
    // be a dependency (it would re-fire the effect forever).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!program || dismissed) return null;

  const total = program.milestones?.length || 0;
  const done = (program.milestones || []).filter((m) => m.status === "completed").length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const started = done > 0;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, program._id);
    setDismissed(true);
  };

  return (
    <div className="px-4 pt-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/programs/${program._id}`)}
        onKeyDown={(e) => e.key === "Enter" && navigate(`/programs/${program._id}`)}
        className="relative cursor-pointer overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 sm:p-5 transition-shadow hover:shadow-md"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-2.5 end-2.5 rounded-md p-1 text-muted-foreground/70 hover:bg-muted hover:text-foreground"
          aria-label={t("dismiss") || "Dismiss"}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex p-3 rounded-lg bg-primary/15 shrink-0">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-primary">
              {started
                ? t("continue_your_program") || "Continue your program"
                : t("start_here") || "Start here"}
            </p>
            <h3 className="text-base sm:text-lg font-semibold truncate">{program.title}</h3>
            {total > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 max-w-[220px] rounded-full bg-primary/15 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {done}/{total}
                </span>
              </div>
            )}
          </div>
          <Button size="sm" className="shrink-0 hidden sm:inline-flex">
            {started ? t("continue") || "Continue" : t("get_started") || "Get started"}
            <ArrowRight className="ms-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingHero;
